import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { X, ChevronRight, ChevronLeft, CheckCircle, Package, MapPin, CreditCard, Bell, Truck, Store, Bike, Scissors, Star, Wallet } from "lucide-react";

// Tutoriels par rôle
const tutorials = {
  client: {
    title: "Bienvenue sur ARTEKNOS !",
    subtitle: "Votre service de livraison express à Dakar",
    color: "from-blue-500 to-blue-600",
    steps: [
      {
        icon: Package,
        title: "Créez votre commande",
        description: "Cliquez sur 'Nouvelle commande' pour envoyer un colis. Indiquez les adresses de retrait et de livraison.",
        image: "📦",
        tip: "Utilisez la carte pour sélectionner précisément les adresses"
      },
      {
        icon: MapPin,
        title: "Calcul automatique des frais",
        description: "Les frais de livraison sont calculés automatiquement selon la distance. Base: 500 FCFA + 150 FCFA/km",
        image: "🗺️",
        tip: "Plus c'est proche, moins c'est cher !"
      },
      {
        icon: Truck,
        title: "Suivez en temps réel",
        description: "Une fois un livreur assigné, suivez sa position GPS en direct sur la carte.",
        image: "🚴",
        tip: "Vous recevrez des notifications à chaque étape"
      },
      {
        icon: CreditCard,
        title: "Paiement flexible",
        description: "Payez en ligne ou à la livraison. C'est vous qui choisissez !",
        image: "💳",
        tip: "Le paiement en ligne est plus rapide"
      },
      {
        icon: Star,
        title: "Évaluez le service",
        description: "Après chaque livraison, notez votre livreur pour aider la communauté.",
        image: "⭐",
        tip: "Vos avis nous aident à améliorer le service"
      }
    ]
  },
  partner: {
    title: "Bienvenue Partenaire !",
    subtitle: "Gérez vos livraisons facilement",
    color: "from-green-500 to-green-600",
    steps: [
      {
        icon: Store,
        title: "Votre tableau de bord",
        description: "Accédez à toutes vos commandes en un coup d'œil. Commandes en attente, en préparation et livrées.",
        image: "🏪",
        tip: "Actualisez régulièrement pour voir les nouvelles commandes"
      },
      {
        icon: Package,
        title: "Acceptez les commandes",
        description: "Quand une commande arrive, cliquez sur 'Accepter et préparer' pour la valider.",
        image: "✅",
        tip: "Plus vous répondez vite, plus vos clients sont satisfaits"
      },
      {
        icon: Bell,
        title: "Marquez comme prête",
        description: "Une fois la commande préparée, cliquez sur 'Commande prête'. Un livreur sera assigné automatiquement.",
        image: "📦",
        tip: "Un livreur proche sera notifié immédiatement"
      },
      {
        icon: Truck,
        title: "Suivi de livraison",
        description: "Suivez le livreur assigné et l'état de la livraison en temps réel.",
        image: "🚴",
        tip: "Vous pouvez contacter le livreur si besoin"
      },
      {
        icon: Star,
        title: "Statistiques",
        description: "Consultez vos revenus, nombre de commandes et performance globale.",
        image: "📊",
        tip: "Analysez vos meilleurs produits"
      }
    ]
  },
  courier: {
    title: "Bienvenue Livreur !",
    subtitle: "Gagnez en livrant à Dakar",
    color: "from-orange-500 to-orange-600",
    steps: [
      {
        icon: CheckCircle,
        title: "Validation admin",
        description: "Votre compte doit être validé par l'admin avant de commencer. Vous serez notifié une fois approuvé.",
        image: "⏳",
        tip: "La validation prend généralement moins de 24h"
      },
      {
        icon: Bike,
        title: "Activez votre disponibilité",
        description: "Cliquez sur 'Disponible' pour recevoir des demandes de livraison près de vous.",
        image: "🟢",
        tip: "Désactivez quand vous faites une pause"
      },
      {
        icon: Package,
        title: "Acceptez des courses",
        description: "Les commandes prêtes s'affichent avec la distance et vos gains potentiels. Acceptez celles qui vous conviennent.",
        image: "📦",
        tip: "Vous voyez votre gain avant d'accepter"
      },
      {
        icon: MapPin,
        title: "Navigation GPS",
        description: "Votre position est partagée en temps réel avec le client. Suivez l'itinéraire optimal.",
        image: "🗺️",
        tip: "Activez votre GPS pour un suivi précis"
      },
      {
        icon: Wallet,
        title: "Vos gains",
        description: "Chaque livraison terminée crédite automatiquement votre portefeuille. Vous gardez 80% des frais de livraison.",
        image: "💰",
        tip: "Commission plateforme: 10%"
      }
    ]
  },
  vendor: {
    title: "Bienvenue Artisan !",
    subtitle: "Gérez vos rendez-vous et livraisons",
    color: "from-purple-500 to-purple-600",
    steps: [
      {
        icon: Scissors,
        title: "Votre agenda",
        description: "Gérez vos rendez-vous clients directement depuis l'application. Planifiez les essayages et livraisons.",
        image: "📅",
        tip: "Synchronisez avec votre calendrier"
      },
      {
        icon: Package,
        title: "Livraison des confections",
        description: "Une fois un vêtement terminé, créez une commande de livraison pour l'envoyer à votre client.",
        image: "👔",
        tip: "Le client peut suivre sa livraison en temps réel"
      },
      {
        icon: MapPin,
        title: "Adresses sauvegardées",
        description: "Les adresses de vos clients réguliers sont sauvegardées pour faciliter les prochaines livraisons.",
        image: "📍",
        tip: "Gagnez du temps sur les commandes récurrentes"
      },
      {
        icon: CreditCard,
        title: "Paiement à la livraison",
        description: "Vos clients peuvent payer à la livraison ou en ligne. Vous recevez le paiement rapidement.",
        image: "💳",
        tip: "Proposez les deux options à vos clients"
      },
      {
        icon: Star,
        title: "Fidélisez vos clients",
        description: "Un service de livraison rapide et fiable fidélise vos clients. Ils reviendront !",
        image: "⭐",
        tip: "Livraison rapide = clients satisfaits"
      }
    ]
  }
};

export default function OnboardingTutorial({ userRole, userName, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const tutorial = tutorials[userRole] || tutorials.client;
  const totalSteps = tutorial.steps.length;
  const step = tutorial.steps[currentStep];
  const StepIcon = step.icon;

  const nextStep = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTutorial();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeTutorial = () => {
    localStorage.setItem(`onboarding_completed_${userRole}`, 'true');
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  const skipTutorial = () => {
    completeTutorial();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg shadow-2xl border-0 overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Header */}
        <div className={`bg-gradient-to-r ${tutorial.color} text-white p-6 relative`}>
          <button
            onClick={skipTutorial}
            className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-full transition-colors"
            data-testid="skip-tutorial-btn"
          >
            <X className="w-5 h-5" />
          </button>

          {currentStep === 0 ? (
            <div className="text-center">
              <div className="text-5xl mb-3">👋</div>
              <h2 className="text-2xl font-bold mb-1">{tutorial.title}</h2>
              <p className="text-white/80">{tutorial.subtitle}</p>
              {userName && <p className="mt-2 font-medium">Bonjour {userName} !</p>}
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center text-4xl">
                {step.image}
              </div>
              <div>
                <p className="text-white/70 text-sm">Étape {currentStep + 1}/{totalSteps}</p>
                <h3 className="text-xl font-bold">{step.title}</h3>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <CardContent className="p-6">
          {currentStep === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted-foreground mb-6">
                Ce tutoriel va vous guider à travers les fonctionnalités principales de l'application.
              </p>
              <div className="flex justify-center gap-2 mb-6">
                {tutorial.steps.map((stepItem, idx) => (
                  <div
                    key={stepItem.title || `step-${idx}`}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === currentStep ? `bg-gradient-to-r ${tutorial.color}` : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="py-2">
              <p className="text-gray-700 mb-4 leading-relaxed">
                {step.description}
              </p>

              {/* Tip box */}
              <div className={`flex items-start gap-3 p-4 rounded-xl bg-gradient-to-r ${tutorial.color} bg-opacity-10`}
                   style={{background: 'linear-gradient(135deg, rgba(37,99,235,0.1), rgba(37,99,235,0.05))'}}>
                <div className="text-2xl">💡</div>
                <div>
                  <p className="font-medium text-sm text-gray-800">Astuce</p>
                  <p className="text-sm text-gray-600">{step.tip}</p>
                </div>
              </div>

              {/* Progress dots */}
              <div className="flex justify-center gap-2 mt-6">
                {tutorial.steps.map((stepItem, idx) => (
                  <button
                    key={stepItem.title || `dot-${idx}`}
                    onClick={() => setCurrentStep(idx)}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                      idx === currentStep
                        ? 'w-8 bg-primary'
                        : idx < currentStep
                          ? 'bg-primary/50'
                          : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex gap-3 mt-6">
            {currentStep > 0 && (
              <Button
                variant="outline"
                onClick={prevStep}
                className="flex-1 rounded-full"
                data-testid="prev-step-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Précédent
              </Button>
            )}

            <Button
              onClick={nextStep}
              className={`flex-1 rounded-full bg-gradient-to-r ${tutorial.color} hover:opacity-90 text-white`}
              data-testid="next-step-btn"
            >
              {currentStep === 0 ? (
                "Commencer le tutoriel"
              ) : currentStep === totalSteps - 1 ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Terminer
                </>
              ) : (
                <>
                  Suivant
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>

          {/* Skip link */}
          {currentStep < totalSteps - 1 && (
            <button
              onClick={skipTutorial}
              className="w-full mt-4 text-sm text-muted-foreground hover:text-gray-700 transition-colors"
              data-testid="skip-tutorial-link"
            >
              Passer le tutoriel
            </button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Hook pour vérifier si le tutoriel doit être affiché
export function useOnboarding(userRole) {
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (userRole) {
      const completed = localStorage.getItem(`onboarding_completed_${userRole}`);
      const justRegistered = sessionStorage.getItem('just_registered');

      if (!completed && justRegistered) {
        setShowOnboarding(true);
        sessionStorage.removeItem('just_registered');
      }
    }
  }, [userRole]);

  const completeOnboarding = () => {
    setShowOnboarding(false);
  };

  const resetOnboarding = () => {
    if (userRole) {
      localStorage.removeItem(`onboarding_completed_${userRole}`);
      setShowOnboarding(true);
    }
  };

  return { showOnboarding, completeOnboarding, resetOnboarding };
}
